import type { Alta, Canal, CategoriaMeta, EstadoCanal, EstadoPlantilla, Incidencia, NuevaPlantillaMeta, ParteDePlantilla, PlantillaMeta, TipoCanal, TipoIncidencia, TipoVariable } from '../../api/ops/canales';
import type { EstadoChip } from '../../componentes/Chip';
import type { NombreIcono } from '../../componentes/iconos';
import type { TarjetaAyuda } from '../../marco/cabecera';

/**
 * Reglas y textos de «Canales» (11.1, 11.2 y 11.4), tal cual del diseño (`diseno/canales-asistente/`). Sin estilos: las
 * pantallas los pintan. Lo que el contrato aún no da (ver «Necesita decisión» del README) no está aquí.
 */

/**
 * Tipos de canal que se conectan a mano (11.2). El chat de la web (`web_chat`) no tiene alta manual (G685): Canales lo
 * crea al activarse el bloque Asistente IA y `startChannelConnection` con él responde `422`.
 */
export const TIPOS: readonly TipoCanal[] = ['whatsapp', 'instagram', 'telegram', 'email', 'voice'];
export const TIPO: Readonly<Record<string, { nombre: string; icono: NombreIcono }>> = {
    whatsapp: { nombre: 'WhatsApp', icono: 'message-circle' },
    instagram: { nombre: 'Instagram', icono: 'camera' },
    telegram: { nombre: 'Telegram', icono: 'send' },
    email: { nombre: 'Email', icono: 'mail' },
    web_chat: { nombre: 'Chat de la web', icono: 'globe' },
    voice: { nombre: 'Voz', icono: 'phone' },
};
/** Segunda línea del chat de la web en 11.1 (G685), en lugar de «tipo · identificador». */
export const LINEA_CHAT_WEB = 'Chat de la web · incluido con el Asistente IA';
/**
 * Ayuda de «Dominios permitidos» del chat (11.1, G972): el servidor acepta también los dominios de la clave del widget
 * `canales.chat_web` y, con `allowed_origins` vacío, los de la clave de la web. Texto del dibujo.
 */
export const AYUDA_DOMINIOS_CHAT = 'Uno por línea. Valen siempre las del widget del chat (Web pública › Widgets); si no pones ninguno aquí, también los de la clave de tu web.';

/** Tipos que se pintan en la lista de 11.1: los que se conectan a mano y el chat de la web (G685). Los que lleguen nuevos, no. */
export function esTipoDibujado(t: string): boolean {
    return (TIPOS as readonly string[]).includes(t) || t === 'web_chat';
}

/** ¿Se puede volver a conectar a mano? El chat de la web no (G685: sin alta manual). */
export function tieneAltaManual(t: string): boolean {
    return (TIPOS as readonly string[]).includes(t);
}

/** `messaging_limit_tier` (lista cerrada de Meta, G687) con los textos de G716; sin cifras de dinero ni promesas. */
export const LIMITE_META: Readonly<Record<NonNullable<Canal['messaging_limit_tier']>, string>> = {
    tier_250: 'Hasta 250 conversaciones nuevas al día',
    tier_1k: 'Hasta 1.000 conversaciones nuevas al día',
    tier_10k: 'Hasta 10.000 conversaciones nuevas al día',
    tier_100k: 'Hasta 100.000 conversaciones nuevas al día',
    tier_unlimited: 'Sin límite de conversaciones nuevas al día',
};
/** La ayuda del nivel de Meta (G716). */
export const AYUDA_LIMITE_META = 'Meta lo sube sola si tu número envía con buena calidad';

/** Estado del Asistente (`Assistant.status`) en la ficha del chat de la web (G715), con los textos de 11.5. */
export const ESTADO_ASISTENTE: Readonly<Record<string, [string, EstadoChip]>> = {
    attending: ['Atendiendo', 'confirmed'],
    off: ['Apagado', 'neutral'],
    not_configured: ['Sin publicar', 'draft'],
    no_entitlement: ['Sin el bloque Asistente IA', 'neutral'],
    quota_exhausted: ['Cupo agotado', 'problem'],
    season_pause: ['En pausa', 'pending'],
    read_only: ['Solo lectura', 'problem'],
};

/** Los dominios del chat de la web en una línea, sin `https://` (vacío: los de la clave pública de la web, G715). */
export function dominiosDelChat(c: Pick<Canal, 'allowed_origins'>): string {
    const lista = c.allowed_origins ?? [];
    return lista.length === 0 ? 'Los de tu web' : lista.map((o) => o.replace(/^https?:\/\//, '').replace(/\/+$/, '')).join(', ');
}

/**
 * Lo escrito en «Dominios permitidos» (uno por línea) como `allowed_origins`: cada uno, el origen (`https://` si no lo
 * lleva). `null` si alguno no es una dirección válida.
 */
export function origenesDe(texto: string): string[] | null {
    const lineas = texto.split(/[\s,]+/).map((l) => l.trim()).filter((l) => l !== '');
    const origenes: string[] = [];
    for (const l of lineas) {
        try {
            const u = new URL(/^https?:\/\//i.test(l) ? l : `https://${l}`);
            if (!u.hostname.includes('.') && u.hostname !== 'localhost') {
                return null;
            }
            if (!origenes.includes(u.origin)) {
                origenes.push(u.origin);
            }
        } catch {
            return null;
        }
    }
    return origenes;
}

/** Desde dónde sale el email (G721, `Channel.email_sender`): siempre desde Reservaria con el nombre del negocio (G747). */
export function remitenteDe(c: Pick<Canal, 'email_sender'>): { direccion: string; sub: string } | null {
    const r = c.email_sender ?? null;
    if (r === null) {
        return null;
    }
    return { direccion: `${r.from_name} <${r.from_address}>`, sub: 'Desde Reservaria, con el nombre de tu negocio' };
}
/** Adónde van las respuestas a los emails (G721, G447). */
export const RESPUESTAS_EMAIL = 'Las respuestas de tus clientes llegan a tu buzón.';

export const ESTADO_CANAL: Readonly<Record<EstadoCanal, [string, EstadoChip]>> = {
    pending: ['Pendiente', 'pending'],
    verifying: ['En verificación', 'pending'],
    active: ['Activo', 'confirmed'],
    degraded: ['Degradado', 'pending'],
    down: ['Caído', 'problem'],
    error: ['Error', 'problem'],
    disconnected: ['Desconectado', 'neutral'],
};
export const CALIDAD: Readonly<Record<string, [string, EstadoChip]>> = { green: ['Alta', 'confirmed'], yellow: ['Media', 'pending'], red: ['Baja', 'problem'], unknown: ['Sin valorar', 'neutral'] };
/** `meta_credit_line_status` (G45, G46, G559: sin moneda). `shared` = pago de YCloud activo: lo pone Reservaria. */
export const PAGO: Readonly<Record<string, [string, EstadoChip]>> = {
    shared: ['Lo pone Reservaria', 'confirmed'],
    pending: ['Activándose', 'pending'],
    blocked_payment_method: ['Tu cuenta tiene otra forma de pago', 'problem'],
    error: ['No se ha podido activar', 'problem'],
    not_applicable: ['No aplica', 'neutral'],
};
export const PROVEEDOR: Readonly<Record<string, string>> = {
    ycloud: 'YCloud, socio oficial de Meta',
    meta: 'Meta',
    telegram: 'Telegram',
    mail_server: 'Servidor de correo de Reservaria',
    voice_provider: 'Proveedor de telefonía',
    reservaria: 'Reservaria',
};
/** Icono, título, texto y si pide reconectar. */
export const INCIDENCIA: Readonly<Record<TipoIncidencia, [NombreIcono, string, string, boolean]>> = {
    token_expired: ['key-round', 'El acceso ha caducado', 'Hay que volver a conectar el canal.', true],
    account_restricted: ['ban', 'Meta ha restringido la cuenta', 'Revisa la cuenta en Meta y vuelve a conectarla.', true],
    quality_low: ['trending-down', 'Calidad baja en Meta', 'Muchos clientes bloquean o marcan como spam. Meta puede bajar el límite diario.', false],
    limit_reduced: ['gauge', 'Meta ha bajado el límite diario', 'Se pueden iniciar menos conversaciones al día.', false],
    webhook_failing: ['wifi-off', 'No llegan los mensajes', 'El servicio no nos entrega los avisos de mensajes nuevos.', true],
    bounces_high: ['mail-x', 'Muchos emails rebotados', 'Revisa los emails de tus clientes con rebotes.', false],
    number_inactive: ['phone', 'El número no está activo', 'Hay que volver a conectar el número.', true],
    acks_missing: ['clock', 'Envíos sin acuse de entrega', 'Algunos mensajes no han confirmado la entrega.', false],
    credit_line_missing: ['wallet', 'El pago de WhatsApp no está activo', 'Sin él no sale ningún WhatsApp. Lo pone Reservaria: lo estamos resolviendo y no tienes que pagar nada.', false],
    business_app_unlinked: ['unlink', 'Se ha desconectado de la app WhatsApp Business', 'WhatsApp lo ha desvinculado: la app llevaba tiempo sin usarse, se abrió en otro móvil o se desconectó desde la app. Vuelve a conectarlo; no cuenta como un número nuevo.', true],
    ice_breakers_missing: ['message-circle-question', 'Faltan los mensajes de inicio de Instagram', 'Sin ellos, los enlaces de las listas no funcionan en chats nuevos. Vuelve a conectar la cuenta.', true],
};

export const AYUDA_CANALES: readonly TarjetaAyuda[] = [
    { icono: 'messages-square', titulo: 'Canales', texto: 'Por donde te escriben tus clientes y por donde les llegan las confirmaciones, pases y recordatorios.' },
    { icono: 'gauge', titulo: 'Salud', texto: 'Lo comprobamos cada 5 minutos. Si uno se cae, lo que iba por él sale por email y te avisamos en la campana.' },
    { icono: 'shield-check', titulo: 'Conexiones oficiales', texto: 'WhatsApp a través de YCloud, socio oficial de Meta; Instagram con Meta; Telegram con tu bot y email desde Reservaria con el nombre de tu negocio. Las claves se guardan cifradas.' },
    { icono: 'package', titulo: 'WhatsApp sin tarjeta', texto: 'Meta no te cobra: los mensajes de WhatsApp van incluidos en tu plan y, si te quedas corto, en paquetes extra.' },
];
export const AYUDA_PLANTILLAS: readonly TarjetaAyuda[] = [
    { icono: 'message-square-text', titulo: 'Plantillas de Meta', texto: 'Para escribir por WhatsApp a un cliente que no te ha escrito en 24 horas, Meta exige una plantilla aprobada.' },
    { icono: 'badge-check', titulo: 'Las de Reservaria', texto: 'Confirmaciones, pases y recordatorios: Reservaria las envía a Meta al conectar WhatsApp y con cada texto nuevo. Se cambian en Mensajes › Mensajes a clientes.' },
    { icono: 'megaphone', titulo: 'Las tuyas', texto: 'Solo de marketing, con el bloque Mensajes y campañas. Llegan a quien aceptó promociones.' },
    { icono: 'lock', titulo: 'No se editan', texto: 'Un texto nuevo es otra plantilla. Si Meta la rechaza, ves el motivo y envías otra corregida.' },
];

// ───────────────────────── 11.1 ─────────────────────────

/** Bloques activos del negocio (`listEntitlements`); null si no se han podido leer. */
export type Bloques = ReadonlySet<string> | null;

export interface Permitido {
    ok: boolean;
    /** Texto y tono de la etiqueta del tipo (vacía si no se saben los bloques). */
    chip: [string, EstadoChip] | null;
    /** Por qué no se puede (sin el bloque). */
    falta?: string;
    proximamente?: boolean;
    /** Instagram solo para las listas de invitados (Listas y RRPP sin el bloque Instagram, G52; tabla de startChannelConnection). */
    soloListas?: boolean;
}

/** Qué bloque permite conectar cada tipo (tabla de `startChannelConnection`: G12, G25, G32, G52). */
export function permitido(tipo: TipoCanal, bloques: Bloques): Permitido {
    if (tipo === 'voice') {
        return { ok: false, proximamente: true, chip: ['Próximamente', 'neutral'], falta: 'Voz IA llega pronto y necesitará el bloque Asistente IA.' };
    }
    if (tipo === 'email') {
        return { ok: true, chip: ['Incluido', 'confirmed'] };
    }
    if (bloques === null) {
        // Sin los bloques no se sabe: se deja intentar y la API responde.
        return { ok: true, chip: null };
    }
    const tiene = (b: string) => bloques.has(b);
    if (tipo === 'whatsapp') {
        return tiene('asistente_ia') || tiene('mensajes_campanas') ? { ok: true, chip: ['Incluido', 'confirmed'] } : { ok: false, chip: ['Asistente o Mensajes', 'neutral'], falta: 'Necesita el bloque Asistente IA o el de Mensajes y campañas.' };
    }
    if (tipo === 'telegram') {
        return tiene('asistente_ia') ? { ok: true, chip: ['Incluido', 'confirmed'] } : { ok: false, chip: ['Asistente IA', 'neutral'], falta: 'Telegram va incluido en el bloque Asistente IA.' };
    }
    if (tipo === 'instagram') {
        if (tiene('instagram')) {
            return { ok: true, chip: ['Incluido', 'confirmed'] };
        }
        if (tiene('listas_rrpp')) {
            return { ok: true, soloListas: true, chip: ['Solo listas', 'neutral'] };
        }
        return { ok: false, proximamente: true, chip: ['Próximamente', 'neutral'], falta: 'El bloque Instagram (con el Asistente IA) llega pronto. Con Listas y RRPP ya puedes conectarlo para las listas de invitados.' };
    }
    return { ok: false, chip: null };
}

export const vivo = (c: Canal) => c.status !== 'disconnected';
/** Con incidencias: degradado, caído o con error, o con una incidencia abierta. */
export function conIncidencias(c: Canal, abierta: Incidencia | null): boolean {
    return vivo(c) && (c.status === 'degraded' || c.status === 'down' || c.status === 'error' || abierta !== null);
}

export const FILTROS_CANALES = [
    { valor: 'conectados', etiqueta: 'Conectados' },
    { valor: 'incidencias', etiqueta: 'Con incidencias' },
    { valor: 'desconectados', etiqueta: 'Desconectados' },
] as const;
export type VistaCanales = (typeof FILTROS_CANALES)[number]['valor'];

export const VACIA_CANALES: Readonly<Record<VistaCanales, { icono: NombreIcono; titulo: string; texto: string }>> = {
    conectados: { icono: 'inbox', titulo: 'Todavía no hay canales conectados', texto: 'Mientras tanto, tus emails salen desde Reservaria. Conecta WhatsApp, Instagram o Telegram.' },
    incidencias: { icono: 'check', titulo: 'Ningún canal con incidencias', texto: 'Todo funciona. Lo comprobamos cada 5 minutos.' },
    desconectados: { icono: 'inbox', titulo: 'No hay canales desconectados', texto: 'Los que desconectes se quedan aquí con su historial.' },
};

/** Línea de salud de la lista: lo que se ve, la segunda línea y el tono (`ok`, `aviso`, `caido`). */
export function saludDe(c: Canal, abierta: Incidencia | null, fecha: (iso: string | null | undefined) => string, hace: (iso: string | undefined) => string, soloListas = false): { linea: string; sub: string; tono: 'ok' | 'aviso' | 'caido' | 'apagado' } {
    const comprobado = `Comprobado: ${c.last_check_at == null ? '—' : hace(c.last_check_at)}`;
    if (c.status === 'disconnected') {
        return { linea: `Desconectado el ${fecha(c.disconnected_at)}`, sub: 'El historial se conserva', tono: 'apagado' };
    }
    if (c.status === 'verifying') {
        return { linea: 'Meta está verificando', sub: 'Se activa solo', tono: 'aviso' };
    }
    if (abierta !== null) {
        return { linea: INCIDENCIA[abierta.kind][1], sub: `Desde ${fecha(abierta.opened_at)}`, tono: abierta.severity === 'down' ? 'caido' : 'aviso' };
    }
    if (c.channel_type === 'web_chat') {
        // G715: el chat de la web no tiene salud; la lista dice cuándo fue la última conversación y dónde se abre.
        return { linea: c.last_conversation_at == null ? 'Todavía sin conversaciones' : `Última conversación: ${hace(c.last_conversation_at).toLocaleLowerCase('es')}`, sub: dominiosDelChat(c), tono: 'ok' };
    }
    if (c.channel_type === 'email' && c.email_sender != null) {
        return { linea: c.email_sender.from_address, sub: remitenteDe(c)?.sub ?? '', tono: 'ok' };
    }
    if (c.channel_type === 'whatsapp') {
        // «Calidad alta · Hasta 1.000 al día» (G687: el límite es lista cerrada); sin límite dado por Meta, solo la calidad.
        const limite = c.messaging_limit_tier == null ? '' : ` · ${LIMITE_META[c.messaging_limit_tier]}`;
        return { linea: `Calidad ${(CALIDAD[c.quality_rating ?? 'unknown'] ?? CALIDAD.unknown ?? ['', 'neutral'])[0].toLowerCase()}${limite}`, sub: comprobado, tono: 'ok' };
    }
    if (c.channel_type === 'instagram' && c.ice_breakers_configured_at != null) {
        return { linea: soloListas ? 'Solo listas · mensajes de inicio listos' : 'Mensajes de inicio configurados', sub: comprobado, tono: 'ok' };
    }
    return { linea: 'Funciona', sub: comprobado, tono: 'ok' };
}

/** Lo que dice el aviso rojo de un canal caído (G451), arriba de los dos apartados. */
export function avisoCaido(c: Canal, abierta: Incidencia | null, fecha: (iso: string | null | undefined) => string): { titulo: string; texto: string } {
    const tipo = TIPO[c.channel_type]?.nombre ?? c.channel_type;
    return {
        titulo: `${nombreDe(c)} está caído${abierta === null ? '' : `: ${INCIDENCIA[abierta.kind][1].toLowerCase()}`}`,
        texto: `Desde ${abierta === null ? 'hace poco' : fecha(abierta.opened_at)}. Mientras tanto, lo que iba por ${tipo} sale por email y el asistente no responde por aquí.`,
    };
}

export function nombreDe(c: Canal): string {
    return c.display_name != null && c.display_name !== '' ? c.display_name : (TIPO[c.channel_type]?.nombre ?? c.channel_type);
}

/** Lo que pasa al desconectar (ventana de impacto). */
export function impactoDesconectar(c: Canal, otroDelMismoTipo: boolean): { icono: NombreIcono; texto: string }[] {
    const tipo = TIPO[c.channel_type]?.nombre ?? c.channel_type;
    const filas: { icono: NombreIcono; texto: string }[] = [
        { icono: 'message-circle', texto: `Deja de recibir y enviar mensajes por ${c.display_identifier}.` },
        { icono: 'mail', texto: `Lo que Reservaria enviaba por ${tipo} (confirmaciones, pases, recordatorios) sale por ${otroDelMismoTipo ? `tu otro ${tipo} o por ` : ''}email.` },
        { icono: 'bot', texto: 'El asistente deja de atender por este canal.' },
        { icono: 'history', texto: 'Las conversaciones y su historial se conservan en el buzón.' },
    ];
    if (c.channel_type === 'whatsapp') {
        filas.push({ icono: 'wallet', texto: 'El pago que pone Reservaria sigue en tu cuenta de Meta (no se puede quitar), pero sin canal no sale ningún mensaje ni se gasta nada.' });
    }
    if (c.provider_links?.deactivate_url != null) {
        filas.push({ icono: 'external-link', texto: 'Tu cuenta sigue en Meta con sus plantillas: desde la ficha tienes el enlace para desactivar el servicio allí.' });
    }
    return filas;
}

// ───────────────────────── 11.2 ─────────────────────────

/** Pasos de cada alta (conectar-canal.bpmn). */
export const PASOS: Readonly<Record<string, readonly [string, string][]>> = {
    whatsapp: [
        ['Entra con tu cuenta de Meta', 'Se abre una ventana de YCloud, socio oficial de Meta. Si no tienes cuenta de empresa, la crea ahí mismo.'],
        ['Elige la cuenta y el número', 'La cuenta de empresa de tu negocio y el número con el que te escriben.'],
        ['Verifica el número', 'Meta te manda un código por SMS o llamada. No te pide tarjeta: el resto lo configuramos nosotros.'],
    ],
    instagram: [
        ['Entra con tu cuenta de Meta', 'Se abre una ventana de Meta.'],
        ['Elige la cuenta de Instagram', 'Tiene que ser una cuenta profesional.'],
        ['Da permiso para los mensajes', 'Configuramos los mensajes de inicio de la cuenta.'],
    ],
    telegram: [
        ['Abre @BotFather en Telegram', 'Es el bot oficial de Telegram para crear bots.'],
        ['Escribe /newbot y ponle nombre', 'Por ejemplo, «Caseta Embrujo». Te dará un token.'],
        ['Pega aquí el token', 'Comprobamos el bot y lo dejamos escuchando.'],
    ],
    email: [
        ['Escribe el nombre del remitente', 'Lo que ven tus clientes en «De». Por defecto, el de tu negocio.'],
        ['Sale desde avisos@reservaria.app', 'Sin registros que añadir: queda listo en el momento.'],
        ['Las respuestas llegan a tu buzón', 'Las contestas desde Reservaria.'],
    ],
};

export const NOTAS_ALTA: Readonly<Record<string, readonly [NombreIcono, string][]>> = {
    whatsapp: [
        ['shield-check', 'Lo conectamos a través de YCloud, socio oficial de Meta. Tú solo entras con Meta y verificas el número.'],
        ['package', 'Sin tarjeta: Meta no te cobra. El pago lo pone Reservaria y los mensajes van incluidos en tu plan.'],
        ['badge-check', 'Al terminar enviamos a Meta las plantillas de tus módulos (confirmaciones, pases…).'],
    ],
    instagram: [['info', 'Configuramos los mensajes de inicio de la cuenta: sin ellos los enlaces de lista no funcionan en chats nuevos.']],
    email: [
        ['info', 'Tus emails salen siempre desde avisos@reservaria.app con el nombre de tu negocio.'],
        ['inbox', RESPUESTAS_EMAIL],
    ],
    telegram: [['lock', 'El token se guarda cifrado y nunca se vuelve a enseñar.']],
};

/** Línea de cada tipo en «Elige el canal». */
export function lineaElegir(tipo: TipoCanal, p: Permitido): string {
    return { whatsapp: 'Entrar con Meta y verificar el número', instagram: p.soloListas === true ? 'Solo para las listas de invitados' : 'Entrar con Meta y elegir la cuenta', telegram: 'Pegar el token de tu bot', email: 'El nombre del remitente', voice: 'Elegir el número (próximamente)' }[tipo as string] ?? '';
}

/** Línea de cada tipo en «Conectar otro canal» de la lista (`n` = conectados de ese tipo). */
export function lineaConectarOtro(tipo: TipoCanal, p: Permitido, n: number): string {
    switch (tipo) {
        case 'whatsapp':
            return n > 0 ? `Otro número (${String(n)} conectado${n > 1 ? 's' : ''})` : 'Con tu número, entrando con Meta';
        case 'instagram':
            return p.soloListas === true ? 'Para los enlaces de las listas' : n > 0 ? 'Otra cuenta' : 'Con tu cuenta profesional';
        case 'telegram':
            return n > 0 ? 'Otro bot' : 'Con el token de tu bot';
        case 'email':
            return 'Desde Reservaria';
        case 'voice':
            return 'Un número para el asistente';
        default:
            return '';
    }
}

/** El token de @BotFather, como lo comprueba el diseño antes de enviarlo. */
export function errorToken(token: string): string | null {
    const t = token.trim();
    if (t === '') {
        return 'Pega el token que te ha dado @BotFather.';
    }
    return /^\d{6,12}:[\w-]{20,}$/.test(t) ? null : 'Ese token no es válido. Cópialo entero desde @BotFather.';
}

/** Lo que sale mal en un alta, tal como lo devuelve la API (`failure_reason` o el error de start/complete). */
export interface Motivo {
    titulo: string;
    texto: string;
    icono: NombreIcono;
    /** Sin «Repetir el alta»: «Volver a Canales». */
    sinReintento?: boolean;
    /** «Añadir a mi plan» al Propietario. */
    plan?: boolean;
}

export const MOTIVOS: Readonly<Record<string, Motivo>> = {
    'forma-de-pago': { titulo: 'Tu cuenta de WhatsApp tiene su propia forma de pago', texto: 'Ya usabas WhatsApp Business con tu tarjeta. Quítala en Meta (Configuración del negocio › Pagos) y repite el alta: con Reservaria no pagas a Meta.', icono: 'credit-card' },
    'pago-ycloud': { titulo: 'No hemos podido activar el pago de WhatsApp', texto: 'El pago lo pone Reservaria y Meta no lo ha aceptado esta vez. No se ha conectado nada y no se te cobra nada. Repite el alta en unos minutos.', icono: 'wallet' },
    'numero-usado': { titulo: 'Este número ya está en otro negocio', texto: 'Un número de WhatsApp solo puede estar en un negocio de Reservaria. Desconéctalo allí o usa otro número.', icono: 'unlink' },
    verificacion: { titulo: 'Meta no ha podido verificar el número', texto: 'El código no ha llegado o no era el bueno. Comprueba que el número recibe SMS o llamadas y repite el alta. No se ha conectado nada.', icono: 'shield-alert' },
    limite: { titulo: 'Ahora mismo no se pueden conectar más números de WhatsApp', texto: 'Reservaria ha llegado al máximo de cuentas de WhatsApp que tiene con YCloud y lo está ampliando. Tus canales siguen funcionando. Te avisamos en la campana cuando puedas conectarlo.', icono: 'users', sinReintento: true },
    bloque: { titulo: 'Tu plan ya no incluye este canal', texto: 'Para conectar WhatsApp hace falta el bloque Asistente IA o el de Mensajes y campañas.', icono: 'puzzle', sinReintento: true, plan: true },
    'varios-numeros': { titulo: 'Tu cuenta de WhatsApp tiene varios números', texto: 'Tu cuenta de WhatsApp tiene varios números; escríbenos y lo conectamos.', icono: 'users', sinReintento: true },
    enlace: { titulo: 'El enlace de alta ya no vale', texto: 'Ha caducado o ya se ha usado. No se ha conectado nada: repite el alta y te damos uno nuevo.', icono: 'unlink' },
    proveedor: { titulo: 'Meta o YCloud no responden', texto: 'No se ha conectado nada. Suele arreglarse en unos minutos: repite el alta.', icono: 'wifi-off' },
    token: { titulo: 'El token no vale', texto: 'Lo has cambiado en @BotFather o el bot se ha borrado. Pega el token nuevo.', icono: 'key-round' },
    'bot-usado': { titulo: 'Este bot ya está en otro negocio', texto: 'Un bot de Telegram solo puede estar en un negocio de Reservaria. Crea otro con @BotFather.', icono: 'unlink' },
};

/** El motivo del diseño para un alta `failed` (el diseño cae en «verificación» si no tiene uno propio). */
export function motivoDeFallo(alta: Pick<Alta, 'failure_reason' | 'channel_type'>): string {
    switch (alta.failure_reason) {
        case 'payment_method_present':
            return 'forma-de-pago';
        case 'credit_line_error':
            return 'pago-ycloud';
        case 'already_connected_elsewhere':
            return alta.channel_type === 'telegram' ? 'bot-usado' : 'numero-usado';
        case 'token_invalid':
            return 'token';
        case 'entitlement_required':
            return 'bloque';
        case 'whatsapp_account_limit_reached':
            return 'limite';
        case 'number_unavailable':
            return alta.channel_type === 'whatsapp' ? 'varios-numeros' : 'verificacion';
        default:
            return 'verificacion';
    }
}

// ───────────────────────── 11.4 ─────────────────────────

export const ESTADO_PLANTILLA: Readonly<Record<EstadoPlantilla, [string, EstadoChip]>> = {
    pending: ['En revisión', 'pending'],
    approved: ['Aprobada', 'confirmed'],
    rejected: ['Rechazada', 'problem'],
    paused: ['En pausa', 'problem'],
    disabled: ['Desactivada', 'problem'],
    withdrawn: ['Retirada', 'neutral'],
};
export const HISTORIAL_PLANTILLA: Readonly<Record<EstadoPlantilla, [NombreIcono, string]>> = {
    pending: ['send', 'Enviada a Meta'],
    approved: ['check', 'Aprobada'],
    rejected: ['circle-x', 'Rechazada'],
    paused: ['pause', 'Pausada'],
    disabled: ['ban', 'Desactivada'],
    withdrawn: ['archive', 'Retirada'],
};
export const CATEGORIA: Readonly<Record<CategoriaMeta, string>> = { utility: 'Utilidad', marketing: 'Marketing', authentication: 'Autenticación' };
export const IDIOMA_PLANTILLA: Readonly<Record<string, string>> = { 'es-ES': 'Español', 'en-GB': 'Inglés' };
export const ORIGEN_PLANTILLA: Readonly<Record<string, string>> = { entregas: 'Reservaria', platform: 'Reservaria', campanas: 'Campañas', panel: 'Tu equipo' };
export const DE_PLANTILLA: Readonly<Record<string, string>> = { entregas: 'Reservaria · Mensajes a clientes', platform: 'Reservaria', campanas: 'Campañas', panel: 'Tu equipo (panel)' };

export const FILTROS_PLANTILLAS = [
    { valor: 'todas', etiqueta: 'Todas', cumple: (p: PlantillaMeta) => p.status !== 'withdrawn' },
    { valor: 'revision', etiqueta: 'En revisión', cumple: (p: PlantillaMeta) => p.status === 'pending' },
    { valor: 'rechazadas', etiqueta: 'Con problemas', cumple: (p: PlantillaMeta) => p.status === 'rejected' || p.status === 'paused' || p.status === 'disabled' },
    { valor: 'retiradas', etiqueta: 'Retiradas', cumple: (p: PlantillaMeta) => p.status === 'withdrawn' },
] as const;
export const VACIA_PLANTILLAS: Readonly<Record<string, [string, string]>> = {
    todas: ['Todavía no hay plantillas', 'Las de Reservaria aparecen al conectar WhatsApp. Las tuyas, cuando envíes la primera.'],
    revision: ['Nada en revisión', 'Las que envíes a Meta se quedan aquí hasta que responda.'],
    rechazadas: ['Ninguna con problemas', 'Aquí verás las rechazadas, con su motivo, y las que Meta pause.'],
    retiradas: ['No hay plantillas retiradas', 'Las que retires de Meta se quedan aquí.'],
};

/**
 * Variables que se pueden insertar, con su nombre, su tipo para Meta (`TemplateVariableKind`, G722) y el ejemplo de la
 * vista previa. Las del catálogo llevan el ejemplo fijo e inventado que pone Canales: el negocio no lo escribe.
 */
export const VARIABLES: readonly { clave: string; nombre: string; ejemplo: string; tipo: TipoVariable }[] = [
    { clave: 'nombre', nombre: 'Nombre del cliente', ejemplo: 'Ana', tipo: 'customer_name' },
    { clave: 'negocio', nombre: 'Nombre del negocio', ejemplo: 'Caseta Embrujo', tipo: 'business_name' },
    { clave: 'fecha', nombre: 'Fecha', ejemplo: 'sábado 8 de noviembre', tipo: 'date' },
    // El ejemplo del enlace es la dirección web del negocio (`public_url`, G118): el panel no conoce dominios.
    { clave: 'enlace', nombre: 'Enlace', ejemplo: '', tipo: 'link' },
];
/** El tipo de una variable (G722): las que no son del catálogo son libres (`free`) y piden ejemplo. */
export function tipoDeVariable(k: string): TipoVariable {
    return VARIABLES.find((v) => v.clave === k)?.tipo ?? 'free';
}
/** Variables que necesitan ejemplo del negocio (G722: solo las libres). */
export function variablesLibres(texto: string): string[] {
    return variablesDe(texto).filter((k) => tipoDeVariable(k) === 'free');
}
/** El aviso del ejemplo de una variable libre (G722). */
export const AVISO_EJEMPLO = 'No pongas datos de clientes reales.';
const NOMBRE_VARIABLE: Readonly<Record<string, string>> = { ...Object.fromEntries(VARIABLES.map((v) => [v.clave, v.nombre])), codigo: 'Código' };
const EJEMPLO_VARIABLE: Readonly<Record<string, string>> = Object.fromEntries(VARIABLES.map((v) => [v.clave, v.ejemplo]));
/** Variables libres del botón «Texto libre» (G754): `libre1`, `libre2`…, con el nombre «Texto libre 1», «Texto libre 2»… */
const LIBRE = /^libre(\d+)$/;
export const nombreVariable = (k: string): string => NOMBRE_VARIABLE[k] ?? (LIBRE.test(k) ? `Texto libre ${k.slice(5)}` : k);
/** La siguiente variable libre que no está en el texto (G754). */
export function siguienteLibre(texto: string): string {
    const usadas = new Set(variablesDe(texto));
    let n = 1;
    while (usadas.has(`libre${String(n)}`)) {
        n++;
    }
    return `libre${String(n)}`;
}
export const BOTON_BAJA = 'No quiero promociones';

/** Las variables `{{x}}` de un texto, en orden y sin repetir. */
export function variablesDe(texto: string): string[] {
    const fuera: string[] = [];
    for (const m of texto.matchAll(/\{\{(\w+)\}\}/g)) {
        const k = m[1] ?? '';
        if (!fuera.includes(k)) {
            fuera.push(k);
        }
    }
    return fuera;
}

/** Trozos del texto para la vista previa: las variables con su ejemplo y marcadas. */
export function trozos(texto: string, ejemplos: Readonly<Record<string, string>> = {}): { texto: string; variable: boolean }[] {
    return texto
        .split(/(\{\{\w+\}\})/)
        .filter((t) => t !== '')
        .map((t) => {
            const m = /^\{\{(\w+)\}\}$/.exec(t);
            if (m === null) {
                return { texto: t, variable: false };
            }
            const k = m[1] ?? '';
            const e = ejemplos[k];
            const fijo = EJEMPLO_VARIABLE[k];
            return { texto: e !== undefined && e !== '' ? e : fijo !== undefined && fijo !== '' ? fijo : k, variable: true };
        });
}

/** Las partes de una plantilla leída: título, mensaje, pie y botones. */
export function partesDe(p: Pick<PlantillaMeta, 'components'>): { titulo: string; cuerpo: string; pie: string; botones: string[] } {
    const c = p.components ?? [];
    const texto = (tipo: ParteDePlantilla['type']) => c.find((x) => x.type === tipo)?.text ?? '';
    return { titulo: texto('header'), cuerpo: texto('body'), pie: texto('footer'), botones: c.filter((x) => x.type === 'buttons').flatMap((x) => (x.buttons ?? []).map((b) => b.text)) };
}

/** Extracto de la lista: «Título · Hola «Nombre del cliente», …». */
export function extracto(p: Pick<PlantillaMeta, 'components'>): string {
    const { titulo, cuerpo } = partesDe(p);
    return (titulo === '' ? '' : `${titulo} · `) + cuerpo.replace(/\{\{(\w+)\}\}/g, (_m, k: string) => `«${nombreVariable(k)}»`);
}

export interface Borrador {
    canal: string;
    nombre: string;
    idioma: string;
    titulo: string;
    conTitulo: boolean;
    cuerpo: string;
    pie: string;
    conPie: boolean;
    ejemplos: Record<string, string>;
}

/** Errores del formulario de una plantilla nueva (los del diseño). `existentes`: nombre e idioma de las que ya hay. */
export function erroresPlantilla(b: Borrador, existentes: readonly { name: string; locale: string }[]): Record<string, string> {
    const e: Record<string, string> = {};
    const nombre = b.nombre.trim();
    if (nombre === '') {
        e.nombre = 'Ponle un nombre.';
    } else if (!/^[a-z0-9_]{1,512}$/.test(nombre)) {
        e.nombre = 'Solo minúsculas, números y _ (sin espacios ni tildes).';
    } else if (existentes.some((p) => p.name === nombre && p.locale === b.idioma)) {
        e.nombre = 'Ya hay una plantilla con ese nombre en ese idioma.';
    }
    if (b.cuerpo.trim() === '') {
        e.cuerpo = 'Escribe el mensaje.';
    } else if (/\{\{(?!(nombre|negocio|fecha|enlace)\}\})/.test(b.cuerpo)) {
        e.cuerpo = 'Usa los botones de «Insertar» para las variables.';
    }
    if (b.conTitulo && /\{\{/.test(b.titulo)) {
        e.titulo = 'El título no puede llevar variables.';
    }
    for (const k of variablesLibres(b.cuerpo)) {
        if ((b.ejemplos[k] ?? '').trim() === '') {
            e[`ejemplo_${k}`] = 'Escribe un ejemplo.';
        }
    }
    return e;
}

/**
 * Lo que se envía a `createMetaTemplate`: desde el panel, solo marketing, con el botón de baja (G448, D12), el tipo de
 * cada variable (`variable_kinds`) y `examples` solo de las libres (G722).
 */
export function plantillaParaEnviar(b: Borrador): NuevaPlantillaMeta & { category: 'marketing'; requested_by: 'panel' } {
    const partes: ParteDePlantilla[] = [];
    if (b.conTitulo && b.titulo.trim() !== '') {
        partes.push({ type: 'header', format: 'text', text: b.titulo.trim() });
    }
    partes.push({ type: 'body', text: b.cuerpo });
    if (b.conPie && b.pie.trim() !== '') {
        partes.push({ type: 'footer', text: b.pie.trim() });
    }
    partes.push({ type: 'buttons', buttons: [{ type: 'opt_out', text: BOTON_BAJA }] });
    const usadas = variablesDe(b.cuerpo);
    const libres = variablesLibres(b.cuerpo);
    return {
        channel_id: b.canal,
        name: b.nombre.trim(),
        locale: b.idioma,
        category: 'marketing',
        components: partes,
        ...(usadas.length > 0 ? { variable_kinds: Object.fromEntries(usadas.map((k) => [k, tipoDeVariable(k)])) } : {}),
        ...(libres.length > 0 ? { examples: Object.fromEntries(libres.map((k) => [k, (b.ejemplos[k] ?? '').trim()])) } : {}),
        requested_by: 'panel',
    };
}

/** «Corregir y enviar otra»: el mismo texto con el nombre versionado (`_v2`, `_v3`…). */
export function nombreVersionado(nombre: string): string {
    const m = /^(.*?)(?:_v(\d+))?$/.exec(nombre);
    const base = m?.[1] ?? nombre;
    const n = m?.[2] === undefined ? 2 : Number(m[2]) + 1;
    return `${base}_v${String(n)}`;
}
